<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { formatLocalizedList } from "@/util/associationUtils";
import { PRODUCT_TYPE } from "@/util/constants";
import {
  collectFieldModels,
  getModelDisplayName,
  normalizeAssociatedModel,
} from "@/util/customObjectUtils";
import { XCircleIcon } from "@gohighlevel/ghl-icons/24/outline";
import { UIModal, UIModalContent, UIModalHeader } from "@gohighlevel/ghl-ui";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Props interface for the mixed custom object error modal
 */
interface Props {
  show: boolean;
  existingObjectName: string; // e.g., "Hospitals"
  attemptedObjectName: string; // e.g., "Doctors"
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "update:close": [value: boolean];
}>();

/**
 * Supporting text with dynamic object names
 */
const { t, locale } = useI18n();

const store = useAppStore();

const productLabel = computed(() => {
  const currentProduct = store.product;
  const label =
    currentProduct === PRODUCT_TYPE.SURVEY
      ? t("survey.typeOfBuilder")
      : currentProduct === PRODUCT_TYPE.QUIZ
      ? t("quiz.typeOfBuilder")
      : t("form.typeOfBuilder");
  return String(label).toLowerCase();
});

const capitalizeFirst = (value: string): string => {
  if (!value || typeof value !== "string") {
    return value as unknown as string;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * Distinct object types already on the form/survey/quiz. The invalid field is
 * removed before this modal opens, so the remaining fields are the objects that
 * block the attempted one (e.g. Contact + Company when adding Opportunity).
 */
const existingObjectNames = computed<string[]>(() => {
  const models = collectFieldModels(
    store.product === PRODUCT_TYPE.FORM ? store.elements : null,
    store.product !== PRODUCT_TYPE.FORM ? store.slides : null
  );

  const names: string[] = [];
  if (store.checkContactFieldExists()) {
    names.push(t("common.contact"));
  }
  const distinctAssociated = Array.from(
    new Set(models.map(normalizeAssociatedModel).filter(Boolean))
  );
  distinctAssociated.forEach((model) => {
    names.push(getModelDisplayName(model as string, store.customObjects || [], t));
  });

  const attempted = capitalizeFirst((props.attemptedObjectName || "").trim());
  return names.filter((name) => name.toLowerCase() !== attempted.toLowerCase());
});

const supportingText = computed(() =>
  t("common.cannotAddThisFieldSubheading", {
    product: productLabel.value,
    existing: formatLocalizedList(existingObjectNames.value, String(locale.value)),
    attempted: capitalizeFirst((props.attemptedObjectName || "").trim()),
  })
);

/**
 * Modal title
 */
const modalTitle = t("common.cannotAddThisField");

/**
 * Handle close action
 */
const handleClose = () => {
  emit("update:close", false);
};
</script>

<template>
  <UIModal :show="show" @update:show="handleClose">
    <template #header>
      <UIModalHeader
        id="mixed-custom-object-error-modal-header"
        :title="modalTitle"
        :icon="XCircleIcon"
        type="error"
        @close="handleClose"
      />
    </template>

    <UIModalContent>
      <div class="text-sm text-gray-600">
        {{ supportingText }}
      </div>
    </UIModalContent>
  </UIModal>
</template>
